/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import ContentShell from "components/ContentShell";
import OperatorSingleSelect from "components/OperatorSingleSelect";
import { useSnackbar } from "hooks/useSnackbar";
import configData from "config/config.json";
import {
  createSchedule,
  getSchedule,
  updateSchedule,
} from "services/reportSchedulerService";
import ColumnSelector from "./components/ColumnSelector";

const useStyles = makeStyles(() => ({
  formContainer: {
    maxWidth: "800px",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  section: {
    padding: "20px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
  },
  sectionTitle: {
    fontWeight: "600 !important",
    marginBottom: "16px !important",
    color: "#333 !important",
  },
  emailChipsContainer: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "6px",
    marginTop: "8px",
    minHeight: "32px",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    paddingTop: "8px",
  },
}));

const FREQUENCY_OPTIONS = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const HOURLY_INTERVAL_OPTIONS = [1, 2, 3, 4, 6, 8, 12];

const TIMEZONE_OPTIONS = [
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Stockholm",
  "Europe/Warsaw",
  "Europe/Lisbon",
  "Europe/Athens",
  "Europe/Helsinki",
  "Europe/Bucharest",
  "Europe/Istanbul",
  "Europe/Moscow",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "America/Mexico_City",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Seoul",
  "Asia/Karachi",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Jakarta",
  "Africa/Cairo",
  "Africa/Lagos",
  "Africa/Johannesburg",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Auckland",
];

const REPORT_TYPE_OPTIONS = [
  { value: "daily_summary", label: "Daily Summary" },
  { value: "transactions", label: "Transactions" },
];

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const ReportSchedulerEditor = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useSnackbar();

  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [populatedOperator, setPopulatedOperator] = useState<{
    _id: string;
    operatorName: string;
    externalOpId?: string;
  } | null>(null);

  // Form state
  const [operatorId, setOperatorId] = useState("");
  const [selectedReportTypes, setSelectedReportTypes] = useState<string[]>([]);
  const [columnsMap, setColumnsMap] = useState<
    Record<string, Array<{ id: string; customLabel?: string }>>
  >({});
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [startDate, setStartDate] = useState<any>(null);
  const [frequency, setFrequency] = useState("");
  const [hourlyInterval, setHourlyInterval] = useState<number>(1);
  const [timezone, setTimezone] = useState("UTC");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [mailFromName, setMailFromName] = useState("");

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit && id) {
      fetchScheduleData(id);
    }
  }, []);

  const fetchScheduleData = async (scheduleId: string) => {
    try {
      setInitialLoading(true);
      const response = await getSchedule(scheduleId);
      if (response?.status && response?.data) {
        const data = response.data;
        if (typeof data.operatorId === "object" && data.operatorId !== null) {
          setOperatorId(data.operatorId._id || "");
          setPopulatedOperator({
            _id: data.operatorId._id,
            operatorName: data.operatorId.operatorName,
            externalOpId: data.operatorId.externalOpId,
          });
        } else {
          setOperatorId(data.operatorId || "");
        }
        const reports: Array<{ type: string; enabled?: boolean; columns: any[] }> =
          data.reports || [];
        setSelectedReportTypes(reports.filter((r: any) => r.enabled !== false).map((r: any) => r.type));
        setColumnsMap(
          reports.reduce((acc: Record<string, any[]>, r: any) => {
            acc[r.type] = r.columns || [];
            return acc;
          }, {}),
        );
        setRecipientEmails(data.recipientEmails || []);
        setStartDate(data.startDate ? dayjs(data.startDate) : null);
        setFrequency(data.frequency || "");
        setHourlyInterval(data.hourlyInterval || 1);
        setTimezone(data.timezone || "UTC");
        setEmailSubject(data.emailSubject || "");
        setEmailBody(data.emailBody || "");
        setMailFromName(data.mailFromName || "");
      }
    } catch (error: any) {
      console.error("Error fetching schedule:", error);
      showSnackbar(error?.message || "Failed to fetch schedule", "error");
      navigate((configData.routes as any).reportSchedulers.list);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleReportTypeToggle = (type: string) => {
    setSelectedReportTypes((prev) => {
      const next = prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type];
      return next;
    });
    if (errors.reportTypes) {
      setErrors((prev) => ({ ...prev, reportTypes: "" }));
    }
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  };

  const addEmail = () => {
    const email = emailInput.trim().replace(/,$/, "");
    if (!email) return;

    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (recipientEmails.includes(email)) {
      setEmailError("This email has already been added");
      return;
    }

    setRecipientEmails((prev) => [...prev, email]);
    setEmailInput("");
    setEmailError("");
    if (errors.recipientEmails) {
      setErrors((prev) => ({ ...prev, recipientEmails: "" }));
    }
  };

  const handleRemoveEmail = (email: string) => {
    setRecipientEmails((prev) => prev.filter((e) => e !== email));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!operatorId) newErrors.operatorId = "Operator is required";
    if (selectedReportTypes.length === 0)
      newErrors.reportTypes = "At least one report type must be enabled";
    const missingColumns = selectedReportTypes.filter(
      (type) => !columnsMap[type] || columnsMap[type].length === 0,
    );
    if (missingColumns.length > 0)
      newErrors.columns = `At least one column must be selected for: ${missingColumns.map((t) => (t === "daily_summary" ? "Daily Summary" : "Transactions")).join(", ")}`;
    if (recipientEmails.length === 0)
      newErrors.recipientEmails = "At least one recipient email is required";
    if (!startDate) newErrors.startDate = "Start date is required";
    if (!frequency) newErrors.frequency = "Frequency is required";
    if (frequency === "hourly" && !hourlyInterval)
      newErrors.hourlyInterval = "Hourly interval is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      // Include all configured report types (enabled or not) so column preferences
      // are preserved in the DB. The backend will only execute enabled ones.
      const allConfiguredTypes = REPORT_TYPE_OPTIONS.map((o) => o.value).filter(
        (type) => columnsMap[type] && columnsMap[type].length > 0,
      );
      // Also include enabled types even if columns aren't in columnsMap yet (validation will catch missing columns)
      const reportTypesInPayload = Array.from(new Set([...allConfiguredTypes, ...selectedReportTypes]));

      const payload: any = {
        operatorId,
        reports: reportTypesInPayload.map((type) => ({
          type,
          enabled: selectedReportTypes.includes(type),
          columns: columnsMap[type] || [],
        })),
        recipientEmails,
        startDate: startDate ? startDate.toISOString() : null,
        frequency,
        timezone,
        emailSubject: emailSubject.trim() || null,
        emailBody: emailBody.trim() || null,
        mailFromName: mailFromName.trim() || null,
      };

      if (frequency === "hourly") {
        payload.hourlyInterval = hourlyInterval;
      }

      let response;
      if (isEdit && id) {
        response = await updateSchedule(id, payload);
      } else {
        response = await createSchedule(payload);
      }

      if (response?.status) {
        showSnackbar(
          response.message ||
            (isEdit
              ? "Schedule updated successfully"
              : "Schedule created successfully"),
          "success",
        );
        navigate((configData.routes as any).reportSchedulers.list);
      }
    } catch (error: any) {
      console.error("Error saving schedule:", error);
      showSnackbar(error?.message || "Failed to save schedule", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate((configData.routes as any).reportSchedulers.list);
  };

  return (
    <ContentShell
      title={isEdit ? "Edit Report Schedule" : "Create Report Schedule"}
      loading={initialLoading}
      showBackButton
      onBackButtonClick={handleCancel}
      actions={
        <Box className={classes.actionButtons}>
          <Button variant="outlined" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || initialLoading}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Box>
      }
    >
      <Box className={classes.formContainer}>
        {/* Operator Selection */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle} variant="subtitle1">
            Operator
          </Typography>
          <OperatorSingleSelect
            label="Select Operator"
            value={operatorId || null}
            onChange={(id) => {
              setOperatorId(id ?? "");
              if (errors.operatorId) {
                setErrors((prev) => ({ ...prev, operatorId: "" }));
              }
            }}
            error={Boolean(errors.operatorId)}
            helperText={errors.operatorId}
            populatedOperator={populatedOperator}
          />
        </Paper>

        {/* Report Types */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle} variant="subtitle1">
            Report Types
          </Typography>
          <FormControl error={Boolean(errors.reportTypes)}>
            <FormLabel>Select one or more report types to include</FormLabel>
            <FormGroup row>
              {REPORT_TYPE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedReportTypes.includes(option.value)}
                      onChange={() => handleReportTypeToggle(option.value)}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
            {errors.reportTypes && (
              <FormHelperText>{errors.reportTypes}</FormHelperText>
            )}
          </FormControl>
        </Paper>

        {/* Column Selection - one per selected report type */}
        {operatorId && selectedReportTypes.length > 0 && (
          <Paper className={classes.section} elevation={0}>
            <Typography className={classes.sectionTitle} variant="subtitle1">
              Columns
            </Typography>
            <Typography
              variant="body2"
              style={{ color: "#666", marginBottom: "16px" }}
            >
              Select columns for each report type. Each report will be sent as a
              separate CSV attachment.
            </Typography>
            {errors.columns && (
              <FormHelperText error style={{ marginBottom: "8px" }}>
                {errors.columns}
              </FormHelperText>
            )}
            {selectedReportTypes.map((type) => {
              const label =
                REPORT_TYPE_OPTIONS.find((o) => o.value === type)?.label ||
                type;
              return (
                <Box key={type} style={{ marginBottom: "16px" }}>
                  <Typography
                    variant="subtitle2"
                    style={{
                      fontWeight: 600,
                      marginBottom: "8px",
                      color: "#555",
                    }}
                  >
                    {label}
                  </Typography>
                  <ColumnSelector
                    reportType={type}
                    selectedColumns={columnsMap[type] || []}
                    onChange={(updated) => {
                      setColumnsMap((prev) => ({ ...prev, [type]: updated }));
                      if (errors.columns) {
                        setErrors((prev) => ({ ...prev, columns: "" }));
                      }
                    }}
                  />
                </Box>
              );
            })}
          </Paper>
        )}

        {/* Recipient Emails */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle} variant="subtitle1">
            Recipient Emails
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Add email (press Enter or comma)"
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
              if (emailError) setEmailError("");
            }}
            onKeyDown={handleEmailKeyDown}
            onBlur={() => {
              if (emailInput.trim()) addEmail();
            }}
            error={Boolean(emailError) || Boolean(errors.recipientEmails)}
            helperText={emailError || errors.recipientEmails}
          />
          {recipientEmails.length > 0 && (
            <Box className={classes.emailChipsContainer}>
              {recipientEmails.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  size="small"
                  onDelete={() => handleRemoveEmail(email)}
                />
              ))}
            </Box>
          )}
        </Paper>

        {/* Start Date */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle} variant="subtitle1">
            Start Date
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              format="DD/MM/YYYY"
              onChange={(date) => {
                setStartDate(date);
                if (errors.startDate) {
                  setErrors((prev) => ({ ...prev, startDate: "" }));
                }
              }}
              slotProps={{
                textField: {
                  size: "small",
                  error: Boolean(errors.startDate),
                  helperText: errors.startDate,
                  style: { width: "100%" },
                },
              }}
            />
          </LocalizationProvider>
        </Paper>

        {/* Timezone */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle} variant="subtitle1">
            Timezone
          </Typography>
          <Typography
            variant="body2"
            style={{ color: "#666", marginBottom: "12px", lineHeight: 1.6 }}
          >
            Controls how "yesterday", "last week", and "last month" are
            interpreted when the report runs. For example, with{" "}
            <strong>Europe/London</strong>, a daily report running at midnight
            UTC will cover the previous calendar day in London time - not UTC.
            This ensures the data aligns with the operator's local business day.
            <br />
            <strong>Note:</strong> This setting has no effect on{" "}
            <strong>Hourly</strong> schedules, which always use UTC intervals.
          </Typography>
          <Autocomplete
            options={TIMEZONE_OPTIONS}
            value={timezone}
            onChange={(_e, val) => setTimezone(val ?? "UTC")}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Timezone" />
            )}
            disableClearable
          />
        </Paper>

        {/* Custom Email */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle} variant="subtitle1">
            Custom Email Content (Optional)
          </Typography>
          <Typography
            variant="body2"
            style={{ color: "#666", marginBottom: "12px", lineHeight: 1.6 }}
          >
            Optionally customise the email subject and body. The report date
            range will be appended automatically. Leave blank to use the default
            text.
          </Typography>
          <Box
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <TextField
              size="small"
              fullWidth
              label="Mail From Name (optional)"
              value={mailFromName}
              onChange={(e) => setMailFromName(e.target.value)}
              inputProps={{ maxLength: 100 }}
              helperText="Sender display name, e.g. 'Playbook Football'. Leave blank to use the default configured name."
            />
            <TextField
              size="small"
              fullWidth
              label="Email Subject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              inputProps={{ maxLength: 200 }}
              helperText={
                emailSubject.trim()
                  ? `Preview: ${emailSubject.trim()} - YYYY-MM-DD to YYYY-MM-DD`
                  : "Default: [Report Type] Report - [Operator] - YYYY-MM-DD to YYYY-MM-DD"
              }
            />
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={3}
              label="Email Body"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              helperText={
                emailBody.trim()
                  ? "The date range line will be added below your text."
                  : 'Default: "Please find attached the [Report Type] report for [Operator]."'
              }
            />
          </Box>
        </Paper>

        {/* Frequency */}
        <Paper className={classes.section} elevation={0}>
          <Typography className={classes.sectionTitle} variant="subtitle1">
            Schedule Frequency
          </Typography>
          <Box style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <FormControl
              size="small"
              style={{ minWidth: 200 }}
              error={Boolean(errors.frequency)}
            >
              <InputLabel>Frequency</InputLabel>
              <Select
                value={frequency}
                label="Frequency"
                onChange={(e) => {
                  setFrequency(e.target.value);
                  if (errors.frequency) {
                    setErrors((prev) => ({ ...prev, frequency: "" }));
                  }
                }}
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.frequency && (
                <FormHelperText>{errors.frequency}</FormHelperText>
              )}
            </FormControl>

            {frequency === "hourly" && (
              <FormControl
                size="small"
                style={{ minWidth: 160 }}
                error={Boolean(errors.hourlyInterval)}
              >
                <InputLabel>Interval (hours)</InputLabel>
                <Select
                  value={hourlyInterval}
                  label="Interval (hours)"
                  onChange={(e) => {
                    setHourlyInterval(Number(e.target.value));
                    if (errors.hourlyInterval) {
                      setErrors((prev) => ({ ...prev, hourlyInterval: "" }));
                    }
                  }}
                >
                  {HOURLY_INTERVAL_OPTIONS.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h} hour{h > 1 ? "s" : ""}
                    </MenuItem>
                  ))}
                </Select>
                {errors.hourlyInterval && (
                  <FormHelperText>{errors.hourlyInterval}</FormHelperText>
                )}
              </FormControl>
            )}
          </Box>
        </Paper>
      </Box>
    </ContentShell>
  );
};

export default ReportSchedulerEditor;
